import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  type VehicleCategory,
  type VehicleCategoryPricingInput,
} from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column } from '@/components/ui';
import { IconCar } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

const PAGE = 50;

/** Vehicle Categories — the ride/vehicle-type reference catalogue ("Tesla Model
 *  3" … with their base-fare / per-km params + onboarding icon keys). content-svc
 *  OWNS the `vehicle_categories` table; the driver app reads it via content-svc's
 *  public GET /v1/content/vehicle-types (CreateVehicleType onboarding).
 *
 *  EDITABLE slice (gated by fares.manage — finance + superadmin; pricing is
 *  finance's domain): the per-km SURCHARGE the premium Tesla models add — the
 *  extra charged to the rider, and the matching amount paid straight to the
 *  driver (the pass-through). An Edit button opens a drawer for those two fields
 *  only. admin-svc proxies content-svc's owning endpoint + audits the write.
 *
 *  READ-ONLY (NOT editable here): base_fare + price_per_km. They are NOT wired
 *  into the live fare calc (the km-credit model + the Fare Rates page own the
 *  real rates), so editing them would be a lying control — they're shown for
 *  reference only.
 *
 *  The section is DEFENSIVE: it shows a clean "not yet available" state when the
 *  vehicle_categories table isn't in this Postgres yet (migration not applied). */
export function VehicleCategories() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<VehicleCategory | null>(null);
  const [editing, setEditing] = useState<VehicleCategory | null>(null);
  const canManage = useAdminPermission('fares.manage');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'vehicle-categories', q, offset],
    queryFn: ({ signal }) => adminApi.vehicleCategories({ q: q || undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;
  const rows = data?.items ?? [];
  const activeCount = rows.filter((c) => c.status === 1).length;
  // The surcharge edit is offered only to fares.manage holders. The server
  // enforces the same gate (403) + 503s when content-svc isn't wired, so this is
  // purely the UX affordance.
  const canEdit = canManage && !unavailable;

  const columns: Array<Column<VehicleCategory>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    {
      key: 'vehicle_type',
      header: 'Vehicle type',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-100">{r.vehicle_type || '—'}</div>
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      cell: (r) => <span className="block max-w-xs truncate text-xs text-slate-300">{r.description || '—'}</span>,
    },
    {
      key: 'cust_surcharge',
      header: 'Cust +$/km',
      align: 'right',
      className: 'border-l border-ink-800',
      cell: (r) => surcharge(r.price_per_km_customer_surcharge),
    },
    {
      key: 'drv_surcharge',
      header: 'Drv +$/km',
      align: 'right',
      cell: (r) => surcharge(r.price_per_km_driver_surcharge),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.status === 1 ? 'ok' : 'neutral'} dot>
          {r.status === 1 ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-2">
          {canEdit ? (
            <button
              type="button"
              onClick={() => setEditing(r)}
              className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
            >
              Edit
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setSelected(r)}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-ink-800"
          >
            View
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Vehicle Categories"
        subtitle="Ride / vehicle types the apps offer — per-km surcharge for the premium models (owned by content-svc)."
        right={
          <div className="flex items-center gap-2">
            <Badge tone={canManage ? 'ok' : 'neutral'}>
              {canManage ? 'editable' : 'read-only'}
            </Badge>
            <SearchBox
              value={q}
              onChange={(v) => {
                setQ(v);
                setOffset(0);
              }}
              placeholder="Search type / description…"
            />
          </div>
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Vehicle categories not yet in Postgres"
            description={
              data?.note ??
              'The vehicle_categories table is owned by content-svc and is not in this Postgres yet.'
            }
            icon={<IconCar className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile label="Categories" value={data?.total ?? 0} hint="all statuses" icon={<IconCar className="h-4 w-4" />} />
            <StatTile label="Active" value={activeCount} hint="offered to riders" tone="ok" />
            <StatTile label="Fleet" value="Tesla-only" hint="product decision" />
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading vehicle categories…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load vehicle categories" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<VehicleCategory>
                  columns={columns}
                  rows={rows}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No vehicle categories"
                      description="No vehicle_categories rows match this search."
                      icon={<IconCar className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </div>
      )}

      {selected ? <VehicleCategoryDrawer category={selected} onClose={() => setSelected(null)} /> : null}
      {editing ? <VehicleCategoryPricingDrawer category={editing} onClose={() => setEditing(null)} /> : null}
    </AdminPage>
  );
}

/** Format a per-km surcharge cell: a positive surcharge as "+$x.xx", a 0 (no
 *  extra for this model) as a muted em-dash. */
function surcharge(v: number) {
  if (!v || v <= 0) return <span className="text-text-muted">—</span>;
  return <span className="metric tabular-nums text-slate-200">+${v.toFixed(2)}</span>;
}

/** Read-only detail drawer for one vehicle category — the full row (both language
 *  variants, the reference fare params, the surcharges, and the onboarding icon
 *  keys). Editing happens in the dedicated pricing drawer (surcharge only). */
function VehicleCategoryDrawer({ category, onClose }: { category: VehicleCategory; onClose: () => void }) {
  const c = category;
  return (
    <Drawer
      title={c.vehicle_type || `Vehicle category #${c.id}`}
      subtitle={`#${c.id} · ${c.status === 1 ? 'Active' : 'Inactive'}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <Card>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Category</div>
          <Field label="ID" value={`#${c.id}`} />
          <Field label="Vehicle type" value={c.vehicle_type || '—'} />
          <Field
            label="Status"
            value={
              <Badge tone={c.status === 1 ? 'ok' : 'neutral'} dot>
                {c.status === 1 ? 'Active' : 'Inactive'}
              </Badge>
            }
          />
        </Card>

        <Card>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Per-km surcharge</div>
          <Field label="Customer surcharge ($/km)" value={c.price_per_km_customer_surcharge > 0 ? `+$${c.price_per_km_customer_surcharge.toFixed(2)}` : '—'} />
          <Field label="Driver surcharge ($/km)" value={c.price_per_km_driver_surcharge > 0 ? `+$${c.price_per_km_driver_surcharge.toFixed(2)}` : '—'} />
          <p className="mt-2 text-[11px] text-text-muted">
            Extra per-km charged to the rider for this model; the driver surcharge is paid straight to the driver.
          </p>
        </Card>

        <Card>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Reference (not in fare calc)</div>
          <Field label="Base fare" value={`$${c.base_fare.toFixed(2)}`} />
          <Field label="Price per km" value={`$${c.price_per_km.toFixed(2)}`} />
          <p className="mt-2 text-[11px] text-text-muted">
            Read-only. The live fare uses the km-credit model + the Fare Rates page; these two are reference fields only.
          </p>
        </Card>

        <Card>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Display</div>
          <Field label="Active icon" value={<code className="text-xs text-slate-400">{c.active_icon || '—'}</code>} />
          <Field label="Inactive icon" value={<code className="text-xs text-slate-400">{c.inactive_icon || '—'}</code>} />
          <div className="border-b border-ink-800 py-2">
            <div className="text-[11px] uppercase tracking-wide text-text-muted">Description</div>
            <p className="mt-1 text-sm text-slate-200">{c.description || '—'}</p>
          </div>
        </Card>
      </div>
    </Drawer>
  );
}

// ─── per-category surcharge edit drawer ──────────────────────

/** The editable form state — the two surcharge fields as strings so the number
 *  inputs bind cleanly (parsed + validated on submit). */
interface SurchargeForm {
  customer: string;
  driver: string;
}

/** VehicleCategoryPricingDrawer — edit ONE category's per-km surcharges (the
 *  customer extra + the driver pass-through). Seeded from the current row. On
 *  success it invalidates the vehicle-categories query + closes. The id + name +
 *  the reference fares are shown read-only context.
 *
 *  RBAC: only finance + superadmin hold fares.manage; this drawer only opens for
 *  them (the page gates the Edit affordance on the same hook). admin-svc enforces
 *  the perm server-side + 503s when content-svc isn't wired, regardless. */
function VehicleCategoryPricingDrawer({ category, onClose }: { category: VehicleCategory; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<SurchargeForm>(() => seedSurcharge(category));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: VehicleCategoryPricingInput) => adminApi.updateVehicleCategoryPricing(category.id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'vehicle-categories'] });
      onClose();
    },
  });

  const onSubmit = () => {
    setClientError(null);
    const built = buildSurchargeInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  return (
    <Drawer
      title={`Surcharge — ${category.vehicle_type || `Type ${category.id}`}`}
      subtitle="Per-km surcharge (customer + driver)"
      onClose={onClose}
    >
      <div className="space-y-4">
        <Card>
          <Field label="Category" value={`${category.vehicle_type || `Type ${category.id}`} · #${category.id}`} />
          <Field label="Base fare (reference)" value={`$${category.base_fare.toFixed(2)}`} />
          <Field label="Price per km (reference)" value={`$${category.price_per_km.toFixed(2)}`} />
          <p className="mt-2 text-[11px] text-text-muted">
            Extra per-km charged to the rider for this model; the driver surcharge is paid straight to the driver.
          </p>
        </Card>

        <Card>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
            Per-km surcharge (AUD)
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SurchargeField
              label="Customer surcharge ($/km)"
              value={form.customer}
              onChange={(v) => setForm((f) => ({ ...f, customer: v }))}
            />
            <SurchargeField
              label="Driver surcharge ($/km)"
              value={form.driver}
              onChange={(v) => setForm((f) => ({ ...f, driver: v }))}
            />
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button type="button" disabled={mutation.isPending} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : 'Save surcharge'}
          </button>
          <button type="button" disabled={mutation.isPending} onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

/** A labelled "$" surcharge input row. Numeric, non-negative; the leading "$" is
 *  decorative (the value is the bare number). */
function SurchargeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <div className="mt-1 flex items-center">
        <span className="mr-1.5 text-sm text-text-muted">$</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          className="input"
        />
      </div>
    </div>
  );
}

/** Seed the form strings from a category's current surcharges. A 0 shows as an
 *  empty input so the staffer enters a deliberate value (or leaves it 0). */
function seedSurcharge(c: VehicleCategory): SurchargeForm {
  const s = (v: number) => (v && v > 0 ? String(v) : '');
  return { customer: s(c.price_per_km_customer_surcharge), driver: s(c.price_per_km_driver_surcharge) };
}

/** Validate + convert the form into a VehicleCategoryPricingInput, or return an
 *  error string. Both fields must be finite, non-negative numbers within the
 *  NUMERIC(6,2) range (< 10000). A BLANK field is treated as 0 (no surcharge) —
 *  unlike the fare-rate form, "blank" here meaningfully = "no extra", so an empty
 *  input is the explicit "this model has no surcharge" choice. Mirrors admin-svc
 *  validation so the staffer sees a precise message instantly; the server
 *  re-checks (UX nicety, not the boundary). */
function buildSurchargeInput(
  f: SurchargeForm,
): { input: VehicleCategoryPricingInput } | { error: string } {
  const fields: Array<[keyof VehicleCategoryPricingInput, string, string]> = [
    ['price_per_km_customer_surcharge', f.customer, 'Customer surcharge'],
    ['price_per_km_driver_surcharge', f.driver, 'Driver surcharge'],
  ];
  const out = {} as VehicleCategoryPricingInput;
  for (const [key, raw, label] of fields) {
    const trimmed = raw.trim();
    const n = trimmed === '' ? 0 : Number(trimmed);
    if (!Number.isFinite(n)) return { error: `${label} must be a number.` };
    if (n < 0) return { error: `${label} must be zero or greater.` };
    if (n >= 10000) return { error: `${label} is too large (max 9999.99).` };
    out[key] = n;
  }
  return { input: out };
}
